import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalculateIcon from '@mui/icons-material/Calculate';
import CompareIcon from '@mui/icons-material/Compare';
import { Link } from 'react-router-dom';
import { trackEvent } from '../analytics';
import { isFeatureEnabled } from '../featureFlags';

/**
 * ToolkitsAccordion component - Expandable sections showcasing available tools
 * Following SEMRush pattern of organized tool categories with clear CTAs
 */
const ToolkitsAccordion = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [expandedAccordion, setExpandedAccordion] = useState(null);

  const handleAccordionChange = (toolId) => (event, isExpanded) => {
    // Only allow accordion changes on mobile
    if (!isDesktop) {
      setExpandedAccordion(isExpanded ? toolId : null);
      if (isExpanded) {
        trackEvent('Toolkit Accordion', 'Expand', toolId);
      }
    }
  };

  const handleCTAClick = (toolName) => {
    trackEvent('Toolkit CTA', 'Click', toolName);
  };

  // Define toolkits with their features and CTAs - only showing our 2 core offerings
  const toolkits = [
    {
      id: 'salary-calculator',
      title: 'Salary Calculator',
      icon: <CalculateIcon />,
      description: 'Calculate your exact take-home pay with visual tax bands and comprehensive deductions',
      features: [
        'Visual tax bands (UK\'s only)',
        'Real-time salary adjustments',
        'Pension contributions included',
        'Student loan deductions',
        'Scottish tax rates supported'
      ],
      ctaText: 'Try Salary Calculator',
      ctaLink: '/salary-calculator',
      enabled: true
    },
    {
      id: 'salary-comparison',
      title: 'Salary Comparison',
      icon: <CompareIcon />,
      description: 'Compare two salaries side-by-side to make informed career decisions',
      features: [
        'Side-by-side salary comparison',
        'Net pay difference calculator',
        'Tax and NI comparison',
        'Visual comparison charts'
      ],
      ctaText: 'Compare Salaries',
      ctaLink: '/salary-comparison',
      enabled: isFeatureEnabled('SALARY_COMPARISON')
    }
  ].filter(toolkit => toolkit.enabled);

  return (
    <Box sx={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
      <Typography
        variant="h2"
        sx={{
          textAlign: 'center',
          mb: 4,
          fontSize: { xs: '2rem', md: '2.5rem' },
          fontWeight: 'bold',
          color: 'text.primary'
        }}
      >
        Choose your calculator
      </Typography>

      <Typography
        variant="body1"
        sx={{
          textAlign: 'center',
          mb: 4,
          color: 'text.secondary',
          fontSize: { xs: '1rem', md: '1.1rem' },
          maxWidth: '600px',
          margin: '0 auto 2rem auto'
        }}
      >
        Calculate, compare, and optimize your UK salary decisions
      </Typography>

      <Box 
        sx={{ 
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 3 },
          maxWidth: '1200px',
          margin: '0 auto',
          alignItems: 'flex-start'
        }}
      >
        {toolkits.map((toolkit) => (
          <Accordion
            key={toolkit.id}
            expanded={isDesktop ? true : expandedAccordion === toolkit.id}
            onChange={handleAccordionChange(toolkit.id)}
            disableGutters
            square
            sx={{
              flex: '1',
              width: '100%', // Ensure full width on mobile
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: 1,
              overflow: 'hidden',

              // remove default pre-divider and ALL outer margins that cause misalignment
              m: 0,
              '&:before': { display: 'none' },

              // kill MUI's default 16px vertical margin when expanded
              '&.Mui-expanded': {
                margin: 0,
                boxShadow: isDesktop ? 1 : 3,
                borderColor: 'primary.main',
                transform: 'none',
                borderRadius: 2
              },

              '&:hover': { borderColor: 'primary.light' },

              // keep intended corner radii
              '& .MuiAccordionSummary-root': { borderRadius: '8px 8px 0 0' },
              '& .MuiAccordionDetails-root': { borderRadius: '0 0 8px 8px' },
              '&.Mui-expanded .MuiAccordionSummary-root': { borderRadius: '8px 8px 0 0' },

              transition: 'all 0.3s ease-in-out'
            }}
          >
            <AccordionSummary
              expandIcon={isDesktop ? null : <ExpandMoreIcon />}
              sx={{
                px: 3,
                py: 2.5,
                minHeight: '120px',
                borderRadius: 'inherit',
                cursor: isDesktop ? 'default' : 'pointer',

                // remove extra vertical margin on content in both states
                '& .MuiAccordionSummary-content': {
                  alignItems: 'flex-start',
                  gap: 2,
                  margin: 0
                },
                '& .MuiAccordionSummary-content.Mui-expanded': { margin: 0 },

                '&:hover': {
                  backgroundColor: isDesktop ? 'transparent' : 'action.hover'
                }
              }}
            >
              <Box
                sx={{
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: 'primary.50'
                }}
              >
                {toolkit.icon}
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, fontSize: { xs: '1.1rem', md: '1.25rem' } }}
                >
                  {toolkit.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ 
                    mt: 0.5, 
                    fontSize: '0.9rem',
                    lineHeight: 1.4
                  }}
                >
                  {toolkit.description}
                </Typography>
              </Box>
            </AccordionSummary>

            <AccordionDetails 
              sx={{ 
                px: 3, 
                pb: 3, 
                pt: 0,
                borderRadius: 'inherit',
                flex: 1,
                display: 'flex'
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, flex: 1 }}>
                <List sx={{ py: 0 }}>
                  {toolkit.features.map((feature, index) => (
                    <ListItem key={index} sx={{ py: 0.3, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <CheckCircleIcon
                          color="success"
                          sx={{ fontSize: '1.1rem' }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={feature}
                        slotProps={{
                          primary: {
                            sx: {
                              fontSize: '0.9rem',
                              color: 'text.primary',
                              lineHeight: 1.3
                            }
                          }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>

                <Box sx={{ display: 'flex', justifyContent: 'stretch', width: '100%', mt: 'auto' }}>
                  <Button
                    component={Link}
                    to={toolkit.ctaLink}
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => handleCTAClick(toolkit.title)}
                    fullWidth
                    sx={{
                      py: 1.8,
                      borderRadius: 2,
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '1rem',
                      boxShadow: 1,
                      backgroundColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                        boxShadow: 2,
                        transform: 'translateY(-1px)'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    {toolkit.ctaText}
                  </Button>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};

export default ToolkitsAccordion;